import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import 'Components/atoms/vm-profile-image/vm-profile-image';
import 'Components/atoms/vm-loading-spinner/vm-loading-spinner';
import 'Components/molecules/vm-confirm-modal/vm-confirm-modal';
import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { CommentAttachmentsController } from './comment-attachments-controller';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import { ACCEPTED_MIME, REJECTION_HEADINGS, isImage } from './vm-video-comment-composer.helpers';
import Styles from './vm-video-comment-composer.scss';

interface SubmitDetail {
    text: string;
    attachmentIds: number[];
    replyToId: number | null;
}

/**
 * Comment composer for the video player page.
 *
 * Mobile (<1024px) — rounded pill (idle) ↔ card (focused); paperclip appears
 * only when focused; send is a circle icon button. Desktop — underline field
 * with paperclip inline on focus + a "Post" button row below. Focused state
 * stays expanded while there's any draft (text OR attachments).
 *
 * Paperclip opens a native multi-file picker for JPG/PNG/GIF/PDF. Picked
 * files pass client-side validation (MIME whitelist + 25 MB per-file cap +
 * 10-file per-comment cap); rejects open a `vm-confirm-modal`. Accepted
 * files upload immediately via {@link CommentAttachmentsController} (the
 * composer owns upload); their server-assigned ids ride out on the
 * `vm-comment-submit` detail as `attachmentIds`.
 *
 * @attr avatar-src   - Avatar image URL.
 * @attr avatar-name  - Author name (initials fallback for `vm-profile-image`).
 * @attr avatar-link  - Optional profile URL for the avatar.
 * @attr video-slug   - Video slug used for the upload endpoint
 *                       (`/api/videos/{slug}/comments/upload/`). Required
 *                       when attachments are picked.
 * @attr placeholder  - Textarea placeholder. Default: "Type your comment here".
 * @attr loading      - Spinner on send + disables textarea/attach. Consumer
 *                       toggles this for the duration of the submit.
 * @attr reply-to-id  - Truthy → renders "Replying to <Name>" chip, auto-focuses
 *                       the textarea, and puts `replyToId` in the submit detail.
 *                       **Property-bind, not attribute-bind:** Lit's bare
 *                       `attr=${null}` sets an empty string that `type: Number`
 *                       coerces to `0`.
 * @attr reply-to-name - Author name shown in the reply chip.
 * @attr variant      - `'default'` (top-of-page use — Post button + chip) or
 *                       `'inline'` (used beneath a specific comment for its
 *                       reply flow — Reply + Cancel buttons, no chip).
 *
 * @csspart pill      - Mobile-only pill / card wrapper.
 * @csspart composer  - Desktop-only composer wrapper.
 * @csspart field     - Desktop-only underline-field wrapper.
 * @csspart textarea  - Inner textarea element.
 * @csspart chips     - Chip strip list (only when there are attachments).
 * @csspart chip      - Individual attachment chip.
 * @csspart chip-uploading - Spinner overlay shown on a chip while its upload is in-flight.
 * @csspart actions   - Mobile-only action row (paperclip + send).
 * @csspart attach    - Paperclip button. Hidden on idle mobile / focus-only on desktop.
 * @csspart send      - Send button. Disabled when the draft is empty.
 * @csspart cancel    - Cancel button (only in `variant="inline"`).
 * @csspart post-row  - Desktop-only Post-button row; also hosts the reply chip on the left.
 * @csspart reply-chip - "Replying to <Name>" cancel button (only in `variant="default"` reply mode).
 * @csspart avatar    - `vm-profile-image` host.
 * @csspart error     - `vm-confirm-modal` for the file-rejection alert.
 *
 * @fires vm-comment-submit - Bubbles + composed + cancelable. Detail:
 *   `{ text: string, attachmentIds: number[], replyToId: number | null }`.
 *   The composer owns attachment upload — files are uploaded on pick and
 *   their server-assigned ids are what ride out on the submit. Call
 *   `e.preventDefault()` to keep the draft while the POST is in flight,
 *   then `clear()` on success (also flip `loading` for the spinner).
 * @fires vm-comment-reply-cancel - Bubbles + composed. Fired on chip ✕ click
 *   (`variant="default"`) or Cancel button click (`variant="inline"`).
 * @fires vm-video-comment-composer:focus - Bubbles + composed. Fired when the
 *   textarea receives focus, so consumers can react to "user is typing here"
 *   across the shadow boundary.
 * @fires vm-video-comment-composer:blur - Bubbles + composed. Companion to
 *   `:focus`; fires even if the composer stays visually expanded (draft is
 *   kept until submit or explicit clear). Suppressed while the native file
 *   picker is up so picker-stolen focus doesn't fire a spurious blur.
 */
@customElement('vm-video-comment-composer')
export class VmVideoCommentComposer extends LitElement {
    static override styles = Styles;

    /** Avatar image URL for the signed-in user. */
    @property({ attribute: 'avatar-src' })
    avatarSrc = '';

    /** Author name — used by `vm-profile-image` for initials fallback. */
    @property({ attribute: 'avatar-name' })
    avatarName = '';

    /** Author profile link — passed through to `vm-profile-image`. */
    @property({ attribute: 'avatar-link' })
    avatarLink = '';

    /** Placeholder for the textarea. */
    @property()
    placeholder = 'Type your comment here';

    /** Video slug — required for uploading attachments on pick. Owned by the
     * consumer that mounts the composer (organism, list-item's inline reply). */
    @property({ attribute: 'video-slug' })
    videoSlug = '';

    /**
     * Spinner on send + disables textarea/attach. Set directly on this
     * specific composer element by the consumer for the duration of the
     * submit (via `event.composedPath()[0]` in the submit handler).
     */
    @property({ type: Boolean, reflect: true })
    loading = false;

    /** ID of the comment being replied to. Null / unset for top-level comments. */
    @property({ type: Number, attribute: 'reply-to-id', reflect: true })
    replyToId: number | null = null;

    /** Author name shown in the "Replying to <Name>" chip. */
    @property({ attribute: 'reply-to-name' })
    replyToName = '';

    /**
     * `'default'` — full composer with reply chip (top-of-page use).
     * `'inline'` — used inline beneath a specific comment for its reply flow:
     * Post-button labeled "Reply", adds a "Cancel" button, drops the chip
     * (the visual anchor to the parent comment makes it redundant).
     * Emits `vm-comment-reply-cancel` on Cancel.
     */
    @property({ reflect: true })
    variant: 'default' | 'inline' = 'default';

    @state() private _value = '';
    @state() private _focused = false;
    @state() private _isDesktop = false;

    @query('textarea') private _textarea!: HTMLTextAreaElement;
    @query('input[type="file"]') private _fileInput!: HTMLInputElement;

    /** Suppresses `_onBlur` while the native picker is up (picker-stolen focus would collapse the card). */
    private _pickerOpen = false;

    private readonly _attachments = new CommentAttachmentsController(this, {
        videoSlug: () => this.videoSlug,
    });

    /** Tracks the previous render's attachment count so `updated()` can refit
     * the textarea when chips are added or removed. Controller state changes
     * don't reach Lit's `changed` map, so we compare snapshots instead. */
    private _lastFileCount = 0;

    private _mq: MediaQueryList | null = null;
    private _onMqChange = (e: MediaQueryListEvent) => {
        this._isDesktop = e.matches;
    };

    override connectedCallback(): void {
        super.connectedCallback();
        this._mq = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);
        this._isDesktop = this._mq.matches;
        this._mq.addEventListener('change', this._onMqChange);
        window.addEventListener('focus', this._onPickerDismiss);
        document.addEventListener('visibilitychange', this._onPickerDismiss);
    }

    override disconnectedCallback(): void {
        this._mq?.removeEventListener('change', this._onMqChange);
        this._mq = null;
        window.removeEventListener('focus', this._onPickerDismiss);
        document.removeEventListener('visibilitychange', this._onPickerDismiss);
        super.disconnectedCallback();
    }

    // Clears `_pickerOpen` on cancel paths (no `change` event fires); without
    // this `_onBlur` stays neutered forever. Both window-focus + visibilitychange
    // to cover desktop + mobile dismissal.
    private _onPickerDismiss = () => {
        if (!this._pickerOpen) return;
        this._pickerOpen = false;
        requestAnimationFrame(() => this._textarea?.focus());
    };

    private _onFocus = () => {
        this._focused = true;
        this.dispatchEvent(new CustomEvent('vm-video-comment-composer:focus', { bubbles: true, composed: true }));
    };

    private _onBlur = () => {
        // Picker-stolen focus isn't a real blur — silence the event so
        // consumers listening for `:blur` don't see a spurious "user left".
        if (this._pickerOpen) return;
        const hasDraft = this._value.trim().length > 0 || this._attachments.files.length > 0;
        if (!hasDraft) {
            this._focused = false;
        }
        this.dispatchEvent(new CustomEvent('vm-video-comment-composer:blur', { bubbles: true, composed: true }));
    };

    private _onInput = (e: Event) => {
        this._value = (e.target as HTMLTextAreaElement).value ?? '';
        this._resizeTextarea();
    };

    /** Auto-grow the textarea within CSS min/max-height caps (1 → 3 rows, then scrolls). */
    private _resizeTextarea() {
        const ta = this._textarea;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = `${ta.scrollHeight}px`;
    }

    override updated(changed: PropertyValues): void {
        // Textarea's effective width changes with attachments / focus / reply mode;
        // re-fit so wrapped lines aren't clipped until the next keystroke.
        const fileCount = this._attachments.files.length;
        const filesChanged = fileCount !== this._lastFileCount;
        this._lastFileCount = fileCount;
        if (filesChanged || changed.has('_focused') || changed.has('replyToId')) {
            this._resizeTextarea();
        }
        if (changed.has('replyToId') && this.replyToId) {
            this._textarea?.focus();
        }
    }

    private _onAttach = () => {
        this._pickerOpen = true;
        this._fileInput.click();
    };

    private _removeChip(file: File) {
        if (!file) return;
        // Cancel in-flight upload + revoke blob URL + filter from list. Server
        // may still receive the bytes but the response is discarded and the
        // resulting attachment id is never referenced (garbage-collected).
        this._attachments.remove(file);
        // Pull focus back after the chip-remove button unmounts.
        this.updateComplete.then(() => this._textarea?.focus());
    }

    private _onFilePick = (e: Event) => {
        this._pickerOpen = false;
        const input = e.target as HTMLInputElement;
        const picked = Array.from(input.files ?? []);
        input.value = '';
        if (!picked.length) {
            // Empty pick — user cancelled at the OS chooser. Return focus so
            // the composer doesn't look stuck in "picker open" visual state.
            requestAnimationFrame(() => this._textarea?.focus());
            return;
        }
        const accepted = this._attachments.add(picked);
        // Accepted path — return focus so the user can continue typing.
        // Rejection branches skip this so the modal keeps focus (Enter=Retry).
        if (accepted) {
            requestAnimationFrame(() => this._textarea?.focus());
        }
    };

    private _onErrorCancel = () => {
        this._attachments.clearRejection();
    };

    private _onErrorRetry = () => {
        this._attachments.clearRejection();
        // Next-frame delay so the modal closes before the picker steals focus.
        requestAnimationFrame(() => {
            this._pickerOpen = true;
            this._fileInput.click();
        });
    };

    /** Focuses the internal textarea (host `.focus()` doesn't delegate into shadow DOM). */
    public override focus(): void {
        this._textarea?.focus();
    }

    /** Clears the textarea + attachments (revokes cached blob URLs, aborts
     * any in-flight uploads), and dismisses any open rejection modal. Call
     * after a successful submit. */
    public clear(): void {
        this._value = '';
        this._attachments.clear();
        this._focused = false;
        this.updateComplete.then(() => this._resizeTextarea());
    }

    private _onCancelReply = () => {
        if (this.loading) return;
        this.dispatchEvent(
            new CustomEvent('vm-comment-reply-cancel', {
                bubbles: true,
                composed: true,
            }),
        );
    };

    private _onSend = () => {
        if (this.loading || this._attachments.hasUploadInFlight) return;
        const text = this._value.trim();
        if (!text && this._attachments.files.length === 0) return;

        // Order attachmentIds by files order (chip order) so the eventual
        // POST body preserves the visual order the user set up.
        const attachmentIds = this._attachments.files
            .map((f) => this._attachments.uploadOf(f)?.id)
            .filter((id): id is number => id !== undefined);

        const ev = new CustomEvent<SubmitDetail>('vm-comment-submit', {
            detail: { text, attachmentIds, replyToId: this.replyToId || null },
            bubbles: true,
            composed: true,
            cancelable: true,
        });
        // Consumers can preventDefault to keep the draft while posting and call `clear()` on success.
        const handled = this.dispatchEvent(ev);
        if (handled) this.clear();
    };

    // Prevents the textarea from blurring on in-composer button clicks (which
    // would otherwise collapse the card). Attached to the wrapper so
    // disabled-button clicks (which Chrome swallows at the button) still hit it.
    private _onButtonMouseDown = (e: MouseEvent) => {
        e.preventDefault();
    };

    private _renderTextarea() {
        return html`
            <textarea
                class="vm-video-comment-composer__textarea"
                part="textarea"
                rows="1"
                aria-label=${this.placeholder}
                .value=${this._value}
                placeholder=${this.placeholder}
                ?disabled=${this.loading}
                @focus=${this._onFocus}
                @blur=${this._onBlur}
                @input=${this._onInput}
            ></textarea>
        `;
    }

    private _renderAttachButton(size: 'sm' | 'md') {
        return html`
            <button
                type="button"
                class="vm-video-comment-composer__attach vm-video-comment-composer__attach--${size}"
                part="attach"
                aria-label="Attach files"
                ?disabled=${this.loading}
                @click=${this._onAttach}
            >
                <vm-icon-next name="attachment-02"></vm-icon-next>
            </button>
        `;
    }

    private _renderReplyChip() {
        if (!this.replyToId || this.variant === 'inline') return nothing;
        return html`
            <button
                type="button"
                class="vm-video-comment-composer__reply-chip"
                part="reply-chip"
                aria-label="Cancel reply"
                ?disabled=${this.loading}
                @click=${this._onCancelReply}
            >
                <span>Replying to ${this.replyToName}</span>
                <vm-icon-next name="x-close"></vm-icon-next>
            </button>
        `;
    }

    private _renderChipStrip() {
        const files = this._attachments.files;
        if (!files.length) return nothing;
        return html`
            <ul class="vm-video-comment-composer__chips" part="chips" @mousedown=${this._onButtonMouseDown}>
                ${repeat(
                    files,
                    (file) => file,
                    (file) => {
                        const uploading = this._attachments.uploadOf(file)?.id === undefined;
                        return html`
                            <li class="vm-video-comment-composer__chip" part="chip">
                                ${isImage(file)
                                    ? html`<img
                                          class="vm-video-comment-composer__chip-thumb"
                                          data-testid="chip-thumb"
                                          src=${this._attachments.urlFor(file)}
                                          alt=""
                                      />`
                                    : html`<span
                                          class="vm-video-comment-composer__chip-thumb vm-video-comment-composer__chip-thumb--file"
                                          data-testid="chip-thumb"
                                          role="img"
                                          aria-label=${file.name}
                                      >
                                          <vm-icon-next name="file-06" aria-hidden="true"></vm-icon-next>
                                      </span>`}
                                ${uploading
                                    ? html`<span
                                          class="vm-video-comment-composer__chip-uploading"
                                          part="chip-uploading"
                                          aria-label="Uploading ${file.name}"
                                      >
                                          <vm-loading-spinner size="lg"></vm-loading-spinner>
                                      </span>`
                                    : nothing}
                                <button
                                    type="button"
                                    class="vm-video-comment-composer__chip-remove"
                                    aria-label="Remove ${file.name}"
                                    ?disabled=${this.loading}
                                    @click=${() => this._removeChip(file)}
                                >
                                    <vm-icon-next name="x-circle" size="sm"></vm-icon-next>
                                </button>
                            </li>
                        `;
                    },
                )}
            </ul>
        `;
    }

    private _renderMobile(sendDisabled: boolean) {
        const classes = classMap({
            'vm-video-comment-composer__pill': true,
            'is-focused': this._focused || !!this.replyToId,
        });
        return html`
            <div class=${classes} part="pill">
                ${this._renderReplyChip()} ${this._renderChipStrip()}
                <div class="vm-video-comment-composer__main">
                    ${this._renderTextarea()}
                    <div
                        class="vm-video-comment-composer__actions"
                        part="actions"
                        @mousedown=${this._onButtonMouseDown}
                    >
                        ${this._renderAttachButton('md')}
                        <vm-button
                            class="vm-video-comment-composer__send"
                            part="send"
                            variant="solid"
                            color-scheme="primary"
                            size="sm"
                            icon-only
                            aria-label="Post comment"
                            ?disabled=${sendDisabled}
                            ?loading=${this.loading}
                            @click=${this._onSend}
                        >
                            <vm-icon-next slot="icon-leading" name="arrow-up"></vm-icon-next>
                        </vm-button>
                    </div>
                </div>
            </div>
        `;
    }

    private _renderDesktop(sendDisabled: boolean) {
        const classes = classMap({
            'vm-video-comment-composer__composer': true,
            'is-focused': this._focused || !!this.replyToId,
        });
        return html`
            <div class=${classes} part="composer">
                <div class="vm-video-comment-composer__field" part="field">
                    ${this._renderTextarea()}
                    ${this._focused
                        ? html`<div
                              class="vm-video-comment-composer__field-right"
                              @mousedown=${this._onButtonMouseDown}
                          >
                              ${this._renderChipStrip()} ${this._renderAttachButton('sm')}
                          </div>`
                        : nothing}
                </div>
                <div class="vm-video-comment-composer__post-row" part="post-row" @mousedown=${this._onButtonMouseDown}>
                    ${this._renderReplyChip()}
                    ${this.variant === 'inline'
                        ? html`
                              <vm-button
                                  class="vm-video-comment-composer__cancel"
                                  part="cancel"
                                  variant="unstyled"
                                  color-scheme="neutral"
                                  size="sm"
                                  ?disabled=${this.loading}
                                  @click=${this._onCancelReply}
                              >
                                  Cancel
                              </vm-button>
                          `
                        : nothing}
                    <vm-button
                        class="vm-video-comment-composer__send"
                        part="send"
                        variant="solid"
                        color-scheme="primary"
                        size="sm"
                        ?disabled=${sendDisabled}
                        ?loading=${this.loading}
                        @click=${this._onSend}
                    >
                        ${this.variant === 'inline' ? 'Reply' : 'Post'}
                    </vm-button>
                </div>
            </div>
        `;
    }

    /** Rejection modal (validation reject or upload failure). `nothing` when
     * no rejection is active. Handlers are wired to `this`; `_onErrorRetry`
     * closes the modal and re-opens the file picker. */
    private _renderRejectionDialog() {
        const rejection = this._attachments.rejection;
        if (!rejection) return nothing;
        return html`
            <vm-confirm-modal
                part="error"
                open
                heading-icon="alert-triangle"
                confirm-variant="solid"
                confirm-color-scheme="primary"
                heading=${REJECTION_HEADINGS[rejection]}
                cancel-label="Cancel"
                confirm-label="Select another file"
                @vm-cancel=${this._onErrorCancel}
                @vm-confirm=${this._onErrorRetry}
            ></vm-confirm-modal>
        `;
    }

    override render() {
        // Empty draft OR any pending upload disables Post. Waiting on uploads
        // avoids racing the POST past a still-uploading attachment.
        const sendDisabled =
            (this._value.trim().length === 0 && this._attachments.files.length === 0) ||
            this._attachments.hasUploadInFlight;
        return html`
            <vm-profile-image
                class="vm-video-comment-composer__avatar"
                part="avatar"
                .imageSrc=${this.avatarSrc}
                .imageAlt=${this.avatarName}
                .userName=${this.avatarName}
                .link=${this.avatarLink}
            ></vm-profile-image>

            ${this._isDesktop ? this._renderDesktop(sendDisabled) : this._renderMobile(sendDisabled)}

            <input type="file" multiple accept=${ACCEPTED_MIME.join(',')} hidden @change=${this._onFilePick} />

            ${this._renderRejectionDialog()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comment-composer': VmVideoCommentComposer;
    }
    interface HTMLElementEventMap {
        'vm-comment-submit': CustomEvent<SubmitDetail>;
        'vm-comment-reply-cancel': CustomEvent<void>;
        'vm-video-comment-composer:focus': CustomEvent<void>;
        'vm-video-comment-composer:blur': CustomEvent<void>;
    }
}
